'use client';

import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@av/ui/button';
import { ProductSpecificationFilter } from './product-specification-filter';
import { ProductPriceRange } from './product-price-range';
import { ArrowUp, X } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import RefinementValues from './refinement-values';
import { ProductSpecification } from '@av/database';

const ProductFilters = ({
  specifications,
  range,
  query,
}: {
  specifications?: {
    specification: {
      id: number;
      title: string;
      values: { id: number; value: string; displayOrder: number }[];
      products: ProductSpecification[];
    };
  }[];
  range?: { min: number; max: number };
  query?: string;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const topRef = useRef<HTMLDivElement>(null);
  const [buttonVisible, setButtonVisible] = useState(false);

  const clearSearch = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('query'); // Remove the query parameter if it's empty
    searchParams.delete('pagina');
    router.push(`${pathname}?${searchParams.toString()}`, undefined);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setButtonVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      {
        rootMargin: '0px 0px -100px 0px', // Start observing 100px before it goes out of view at the bottom
        threshold: 0,
      }
    );

    if (topRef.current) {
      observer.observe(topRef.current);
    }

    return () => {
      if (topRef.current) {
        observer.unobserve(topRef.current);
      }
    };
  }, []);

  const scrollToTop = () => {
    if (topRef.current) {
      window.scrollTo({
        top:
          window.innerWidth < 640
            ? topRef.current.getBoundingClientRect().top + window.scrollY - 80
            : 0, // Scroll to the element position minus 60px
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <div ref={topRef} />
      <div className="sticky top-20 z-10 -mx-3 space-y-4 bg-background p-3 sm:top-[8.5rem]">
        {!!query && (
          <div className="flex items-center space-x-4">
            <h2 className="text-base font-semibold md:text-xl">“{query}”</h2>
            <Button
              variant="outline"
              className="rounded-full bg-gray-200 text-sm"
              size="sm"
              onClick={clearSearch}
            >
              Zoekopdracht wissen <X className="ml-1 size-4" />
            </Button>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {!!specifications &&
            specifications.map(({ specification }) => (
              <ProductSpecificationFilter key={specification.id} specification={specification} />
            ))}
          <ProductPriceRange range={range} />
          {buttonVisible && (
            <Button
              variant="outline"
              className="ml-auto rounded-full bg-gray-200 text-sm max-sm:fixed max-sm:bottom-3 max-sm:left-1/2 max-sm:-translate-x-1/2"
              size="sm"
              onClick={scrollToTop}
            >
              Terug naar begin <ArrowUp className="ml-1 size-4" />
            </Button>
          )}
        </div>
        <RefinementValues />
      </div>
    </>
  );
};

export default ProductFilters;
